/* @flow */
import * as React from "react";
import { darken, lighten } from "polished";
import { withColorsProvider, type ColorSet } from "./ColorsProvider";
import type { StylesObject } from "./utils/prefixStyles";
import prefixStyles from "./utils/prefixStyles";
import Theme from "./Theme";
import NoStyleButton from "./NoStyleButton";
import Tappable, { ACTIVE_DARKEN_AMOUNT } from "./Tappable";

type ButtonTheme =
  | "alert"
  | "heat"
  | "cool"
  | "overlay"
  | "accept"
  | "reject"
  | "important"
  | "flat"
  | "toolbar"
  | "floating"
  | "round"
  | "minimal"
  | "ghost"
  | "reverse"
  | "link"
  | "outline"
  | "light";

type Props = {
  children?: ?React.Node,
  disabled: ?boolean,
  full: ?boolean,
  style?: ?StylesObject,
  buttonContentStyle?: ?StylesObject,
  theme?: ButtonTheme | Array<ButtonTheme>,
  type?: ?string,
  needsTrustedTap?: ?boolean,
  onTap?: ?() => mixed,
  onClickCapture?: ?(SyntheticMouseEvent<HTMLElement>) => mixed,
  onMouseDown?: ?(SyntheticMouseEvent<HTMLElement>) => mixed,
  onMouseUp?: ?(SyntheticMouseEvent<HTMLElement>) => mixed,
  onTouchStart?: ?(SyntheticTouchEvent<HTMLElement>) => mixed,
  onTouchMove?: ?(SyntheticTouchEvent<HTMLElement>) => mixed,
  onTouchEnd?: ?(SyntheticTouchEvent<HTMLElement>) => mixed,
  forceActiveStyling?: ?boolean,
  colors: ColorSet,
};

type State = {
  active: boolean,
};

class Button extends React.Component<Props, State> {
  static defaultProps = {
    full: false,
    disabled: false,
  };

  state = {
    active: false,
  };

  _calculateStyles(active: boolean) {
    const {
      onTap,
      onClickCapture,
      full,
      disabled,
      style,
      theme,
      forceActiveStyling,
      colors,
    } = this.props;

    const darkenIfActive = (undarkenedColor: string) => {
      if (active || forceActiveStyling) {
        return this._darken(ACTIVE_DARKEN_AMOUNT, undarkenedColor);
      } else {
        return undarkenedColor;
      }
    };

    const lightenIfActive = (unlightenedColor: string) => {
      if (active || forceActiveStyling) {
        return this._lighten(ACTIVE_DARKEN_AMOUNT, unlightenedColor);
      } else {
        return unlightenedColor;
      }
    };

    const { backgroundColor: userBackgroundColor, ...userStyle } = style || {};

    let styles: Object = prefixStyles({
      button: {
        WebkitTapHighlightColor: "rgba(0,0,0,0)",
        userSelect: "none",

        position: "relative",
        display: "inline-block",
        textAlign: "center",
        borderRadius: Theme.Button.RADIUS,
        color: colors.PRIMARY_TEXT,
        backgroundColor: darkenIfActive(colors.PRIMARY),

        outline: "none",
        cursor: onTap || onClickCapture ? "pointer" : "",

        lineHeight: "1em",

        padding: "0.5em",

        // this is only used for the Footer
        pointerEvents: "auto",
        ...Theme.Styles.textEllipsis,
        ...(full
          ? { display: "block", width: "100%", marginBottom: "0.2em" }
          : null),
        ...(disabled ? { pointerEvents: "none", opacity: 0.25 } : null),

        ...(typeof userBackgroundColor === "string"
          ? { backgroundColor: darkenIfActive(userBackgroundColor) }
          : null),
        ...userStyle,
      },
      content: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
    });

    if (theme) {
      let themes;
      if (typeof theme === "string") {
        themes = [theme];
      } else {
        themes = theme;
      }
      themes.forEach((themePart) => {
        switch (themePart) {
          case "alert": {
            Object.assign(styles.button, {
              backgroundColor: darkenIfActive(colors.RED),
            });
            break;
          }
          case "heat": {
            Object.assign(styles.button, {
              backgroundColor: darkenIfActive(colors.HEAT),
            });
            break;
          }
          case "cool": {
            Object.assign(styles.button, {
              backgroundColor: darkenIfActive(colors.COOL),
            });
            break;
          }
          case "overlay": {
            Object.assign(styles.button, {
              backgroundColor: "transparent",
              fontSize: ".8em",
              padding: "1.2em",
              color: darkenIfActive("white"),
            });
            break;
          }
          case "accept": {
            Object.assign(styles.button, {
              backgroundColor: darkenIfActive(colors.ACCEPT),
            });
            break;
          }
          case "reject": {
            Object.assign(styles.button, {
              backgroundColor: darkenIfActive(colors.REJECT),
            });
            break;
          }
          case "important": {
            Object.assign(styles.button, {
              backgroundColor: darkenIfActive(colors.IMPORTANT),
            });
            break;
          }
          case "flat": {
            Object.assign(styles.button, {
              backgroundColor: darkenIfActive("transparent"),
              color: darkenIfActive(colors.PRIMARY),
            });
            break;
          }
          case "toolbar": {
            Object.assign(styles.button, {
              backgroundColor: darkenIfActive("transparent"),
              color: darkenIfActive(colors.SECONDARY),
            });
            break;
          }
          case "floating": {
            Object.assign(styles.button, {
              boxShadow:
                "0 16px 24px 2px rgba(0,0,0,0.14), " +
                "0 6px 30px 5px rgba(0,0,0,0.12), " +
                "0 8px 10px -5px rgba(0,0,0,0.4)",
            });
            break;
          }
          case "round": {
            Object.assign(styles.button, {
              width: "2em",
              height: "2em",
              borderRadius: "50%",
              padding: 0,
            });
            Object.assign(styles.content, { fontSize: "2em", height: "100%" });
            break;
          }
          case "minimal": {
            Object.assign(styles.button, {
              backgroundColor: darkenIfActive("transparent"),
              color: darkenIfActive(colors.IMPORTANT),
            });
            break;
          }
          case "link": {
            Object.assign(styles.button, {
              backgroundColor: "transparent",
              color: colors.ANCHOR,
              textDecoration: "underline",
            });
            break;
          }
          case "ghost": {
            Object.assign(styles.button, {
              backgroundColor: darkenIfActive("transparent"),
              color: darkenIfActive(colors.PRIMARY),
              // Hard inset box shadow is acting as an inner border. If you
              // change it to an actual inner border, a button with text inside
              // is eg. 34px tall instead of 32px (even with box-sizing:
              // border-box). Ghost buttons are often displayed next to normal
              // or important buttons (Ok/Cancel), so them being different
              // heights looks bad.
              boxShadow: `${colors.PRIMARY} 0px 0px 0px 1px inset`,
            });
            break;
          }
          case "outline": {
            Object.assign(styles.button, {
              backgroundColor: darkenIfActive("transparent"),
              color: darkenIfActive(colors.TEXT),
              boxShadow: `${colors.TEXT} 0px 0px 0px 1px inset`,
            });
            break;
          }
          case "light": {
            Object.assign(styles.button, {
              backgroundColor: lightenIfActive("transparent"),
              boxShadow: "white 0px 0px 0px 1px inset",
            });
            break;
          }
          case "reverse": {
            Object.assign(styles.button, {
              backgroundColor: darkenIfActive(colors.PRIMARY_TEXT),
              color: colors.PRIMARY,
            });
            break;
          }
        }
      });
    }

    return styles;
  }

  _darken = (amount: number, color: string) => {
    if (color === "transparent") {
      return `rgba(0, 0, 0, ${amount})`;
    }
    return darken(amount, color);
  };

  _lighten = (amount: number, color: string) => {
    if (color === "transparent") {
      return `rgba(255, 255, 255, ${amount})`;
    }
    return lighten(amount, color);
  };

  render() {
    const {
      /* eslint-disable no-unused-vars */
      buttonContentStyle,
      children,
      disabled,
      full,
      style,
      theme,
      type,
      needsTrustedTap,
      onTap,
      onClickCapture,
      onMouseDown,
      onMouseUp,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      forceActiveStyling,
      ...attrs
      /* eslint-enable no-unused-vars */
    } = this.props;

    return (
      <Tappable onTap={onTap} needsTrustedTap={needsTrustedTap} noDelay>
        {({ active, tapTargetRefHandler }) => {
          const styles = this._calculateStyles(active);

          let ElementType = "div";
          let additionalProps = {};
          if (onTap != null) {
            ElementType = NoStyleButton;
            additionalProps = {
              innerRef: tapTargetRefHandler,
              disabled,
            };
          }

          return (
            <ElementType
              data-component-name="Button"
              {...additionalProps}
              onMouseDown={onMouseDown}
              onMouseUp={onMouseUp}
              onClickCapture={onClickCapture}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              type={type}
              {...attrs}
              style={styles.button}
            >
              <span style={{ ...styles.content, ...buttonContentStyle }}>
                {children}
              </span>
            </ElementType>
          );
        }}
      </Tappable>
    );
  }
}

export default withColorsProvider(Button);



// WEBPACK FOOTER //
// ../nx-components/src/Button.jsx